/**
 * Authenticated main-app and admin paths that require sign-in when accessed
 * while signed out.
 *
 * Used by the unauthenticated catch-all so deep links like /settings do not
 * fall through to the marketing homepage. Keep in sync with App.tsx route
 * registrations (see unauthenticatedProtectedRouteCoverage.test.ts).
 */

import { APP_ROUTES, CONTACTS_PATH } from './routes';

/** Exact pathnames that require authentication (main app shell + settings alias). */
export const PROTECTED_APP_EXACT_PATHS: readonly string[] = [
  APP_ROUTES.overview,
  APP_ROUTES.legacyDashboard,
  CONTACTS_PATH,
  '/event-types',
  '/templates',
  APP_ROUTES.upcomingEvents,
  APP_ROUTES.orders,
  '/scheduled-items',
  APP_ROUTES.account,
  APP_ROUTES.settings,
  APP_ROUTES.connectedAssistants,
  APP_ROUTES.contactUs,
  APP_ROUTES.flowers,
  APP_ROUTES.mailedCards,
];

/**
 * Path prefixes that require authentication (admin portal and threaded support).
 */
export const PROTECTED_APP_PATH_PREFIXES: readonly string[] = [
  `${APP_ROUTES.adminRoot}/`,
  `${APP_ROUTES.contactUs}/`,
];

const protectedExactSet = new Set(PROTECTED_APP_EXACT_PATHS);

/**
 * Returns true when an unauthenticated visitor should be sent to sign-in
 * (with returnTo) instead of the public marketing homepage.
 *
 * @param pathname - URL pathname only (no query or hash)
 */
export function isProtectedAppPath(pathname: string): boolean {
  if (protectedExactSet.has(pathname)) {
    return true;
  }

  if (pathname === APP_ROUTES.adminRoot) {
    return true;
  }

  return PROTECTED_APP_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}
